import React from 'react';
import styles from './ui.module.css';

export type SignedTone = 'pos' | 'neg' | 'flat';

/**
 * Which way a signed figure reads. With `invert`, a positive number is bad —
 * slippage, cost, a shortfall — so it goes red and a negative one green.
 */
export const signedTone = (value: number | null | undefined, invert = false): SignedTone => {
  if (value === null || value === undefined || !Number.isFinite(value) || value === 0) return 'flat';
  const up = value > 0;
  return up !== invert ? 'pos' : 'neg';
};

/** The class for a signed figure — for a DataTable `cellClassName` or any span. */
export const signedClass = (value: number | null | undefined, invert = false): string => {
  const tone = signedTone(value, invert);
  return tone === 'pos' ? styles.signedPos : tone === 'neg' ? styles.signedNeg : '';
};

interface SignedValueProps {
  value: number | null | undefined;
  /** Formats the number. Defaults to String(value). Use @/utils/formatters. */
  format?: (value: number) => React.ReactNode;
  invert?: boolean;
  /** Shown for null/undefined/NaN. */
  empty?: React.ReactNode;
  title?: string;
}

/**
 * A gain/loss figure: green when it is good, red when it is bad, plain at zero.
 * Green and red mean exactly that here — never "buy" and "sell".
 */
const SignedValue: React.FC<SignedValueProps> = ({ value, format, invert = false, empty = '—', title }) => {
  if (value === null || value === undefined || Number.isNaN(value)) return <>{empty}</>;
  const cls = signedClass(value, invert);
  return (
    <span className={cls || undefined} title={title}>
      {format ? format(value) : String(value)}
    </span>
  );
};

export default SignedValue;
